/**
 * The application root: which shell the signed-in user gets.
 *
 * A `client_user` gets the portal, not the office app with things hidden. That split is
 * structural rather than cosmetic — the two shells share no navigation, no KPI strip and no
 * data shape, and the API surfaces they call are separate modules. It is the cheapest way
 * to guarantee that a client can never see a profitability figure.
 */
import { useLayoutEffect, useRef } from "react";
import { RouterProvider } from "@tanstack/react-router";
import { Button, ErrorState, Spinner } from "@onecpa/ui";
import { router } from "./app/router.js";
import { SessionProvider, useSession } from "./app/session.js";
import { useAuth } from "./auth/AuthProvider.js";
import { AcceptInvitation } from "./screens/AcceptInvitation.js";
import { Portal } from "./screens/Portal.js";
import { SignIn } from "./screens/SignIn.js";

export function App() {
  const { user, loading, signOutNow } = useAuth();
  useRouteResetOnUserChange(user?.uid ?? null);

  if (loading) return <FullPage>טוען…</FullPage>;

  // An invitation link is answered before anything else: the recipient may have no
  // membership yet, and every other screen requires one.
  const inviteToken = new URLSearchParams(window.location.search).get("invite");
  if (user && inviteToken) return <AcceptInvitation token={inviteToken} />;

  if (!user) return <SignIn />;

  return (
    <SessionProvider
      fallback={({ loading: sessionLoading, error, noMembership }) => {
        if (sessionLoading) return <FullPage>טוען…</FullPage>;

        if (noMembership) {
          return (
            <FullPage>
              <p className="text-md font-semibold text-ink">החשבון אינו משויך לאף משרד</p>
              <p className="mt-2 max-w-sm text-sm text-ink-muted">
                אם קיבלתם הזמנה, פתחו את הקישור שנשלח אליכם. אחרת יש לפנות למשרד שהזמין אתכם.
              </p>
              <Button className="mt-6" onClick={() => void signOutNow()}>
                יציאה
              </Button>
            </FullPage>
          );
        }

        return (
          <div className="grid min-h-dvh place-items-center bg-paper px-5">
            <div>
              <ErrorState
                title="לא ניתן לטעון את המשתמש"
                message={error?.message ?? "שגיאה לא ידועה"}
              />
              <div className="mt-2 text-center">
                <Button onClick={() => void signOutNow()}>יציאה</Button>
              </div>
            </div>
          </div>
        );
      }}
    >
      <ShellForRole />
    </SessionProvider>
  );
}

/**
 * Sends a newly signed-in person to their own landing screen.
 *
 * The router is created once at module scope and keeps its location across a
 * sign-out, so without this the next person lands wherever the last one left off
 * — a bookkeeper signing in after an owner was on the profitability screen is
 * greeted by a refusal for a page they never asked for.
 *
 * It lives here rather than in `AuthProvider` for two reasons: routing is not
 * authentication's business, and importing the router there would close a cycle
 * through Layout and the session provider.
 *
 * The condition is narrow on purpose — it fires only when a *different* person
 * was signed in earlier in this tab's life. Two cases it must not disturb, both
 * of which report a null user first and would trip a naive "the uid changed"
 * test: a reload, where Firebase says null until it has restored the session,
 * and a deep link followed by a sign-in, where the URL is the one the person
 * asked for.
 *
 * `useLayoutEffect` rather than `useEffect` so the reset lands before paint;
 * otherwise the previous screen shows for a frame.
 */
function useRouteResetOnUserChange(uid: string | null): void {
  const lastSignedInAs = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!uid) return;

    const previous = lastSignedInAs.current;
    lastSignedInAs.current = uid;

    if (previous === null || previous === uid) return;

    // Through the router's own history: changing `window.location` directly
    // leaves the router rendering its remembered route while the address bar
    // claims otherwise.
    if (router.state.location.pathname !== "/") void router.navigate({ to: "/" });
  }, [uid]);
}

/**
 * Chooses the shell.
 *
 * A separate component so that it renders inside SessionProvider and can read the resolved
 * role — the provider cannot branch on its own value.
 */
function ShellForRole() {
  const { role } = useSession();
  if (role === "client_user") return <Portal />;
  return <RouterProvider router={router} />;
}

function FullPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-paper px-5 text-center">
      <div className="flex flex-col items-center">
        <Spinner className="size-5 text-ledger" />
        <div className="mt-3 text-sm text-ink-faint">{children}</div>
      </div>
    </div>
  );
}
